// SPDX-License-Identifier: BUSL-1.1

/**
 * Unified Activity column definitions (sessions + jobs combined).
 *
 * Extracted from `routes/agent-detail.tsx` during the P3b refactor.
 */

import { type ColumnDef } from '@tanstack/react-table';
import type { Session } from '@/types/api';
import { formatDuration, formatTimeAgo, getSessionDuration } from './helpers';
import type { ActivityItem, Job } from './types';

export const activityColumns: ColumnDef<ActivityItem>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span className={`inline-flex rounded px-1.5 py-0.5 text-xs ${
          row.original.type === 'session'
            ? 'bg-purple-900/50 text-purple-300'
            : 'bg-blue-900/50 text-blue-300'
        }`}>
          {row.original.type === 'session' ? 'SESS' : 'JOB'}
        </span>
        <span className="font-mono text-xs text-neutral-300">
          {row.original.id.slice(0, 12)}...
        </span>
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      const statusColors: Record<string, string> = {
        idle: 'bg-yellow-900/40 text-yellow-400',
        running: 'bg-green-900/40 text-green-400',
        active: 'bg-green-900/40 text-green-400',
        completed: 'bg-green-900/40 text-green-400',
        terminated: 'bg-neutral-700 text-neutral-400',
        failed: 'bg-red-900/40 text-red-400',
        pending: 'bg-yellow-900/40 text-yellow-400',
        processing: 'bg-blue-900/40 text-blue-400',
        skipped: 'bg-neutral-700 text-neutral-400',
        cancelled: 'bg-neutral-700 text-neutral-400',
      };
      const colors = statusColors[status] ?? 'bg-neutral-700 text-neutral-400';
      return (
        <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors}`}>
          {status}
        </span>
      );
    },
  },
  {
    id: 'trigger',
    header: 'Trigger',
    cell: ({ row }) => {
      if (row.original.type === 'session') {
        return <span className="text-neutral-400">-</span>;
      }
      const job = row.original as Job;
      return (
        <span className="text-sm text-neutral-300">
          {job.trigger}
          {job.triggerSource && (
            <span className="ml-1 text-neutral-500">({job.triggerSource})</span>
          )}
        </span>
      );
    },
  },
  {
    id: 'duration',
    header: 'Duration',
    cell: ({ row }) => {
      if (row.original.type === 'session') {
        const session = row.original as Session;
        // Show human-readable duration if available
        if (session.durationHuman) {
          return <span className="text-neutral-400">{session.durationHuman}</span>;
        }
        const duration = getSessionDuration(session);
        return <span className="text-neutral-400">{formatDuration(duration)}</span>;
      }
      // For jobs, calculate from created to completed
      const job = row.original as Job;
      if (job.startedAt && job.completedAt) {
        const start = new Date(job.startedAt).getTime();
        const end = new Date(job.completedAt).getTime();
        const durationSecs = Math.floor((end - start) / 1000);
        return (
          <span className="text-neutral-400">{formatDuration(durationSecs)}</span>
        );
      }
      return <span className="text-neutral-400">-</span>;
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => (
      <span className="text-neutral-400">{formatTimeAgo(row.original.createdAt)}</span>
    ),
  },
];
